import { DateTime } from 'luxon'
import Router from 'vue-router'
import store from '@/store'
import uuidValidate from 'uuid-validate'
import Vue from 'vue'
import GTM from '@/utils/gtm'
import { logWarning, logException } from '@/utils/sentry'
import { messages } from '@/i18n'
import {
  BookingConfirmation,
  BookingConfirmationTimeout,
  BookingConfirmationGenericError,
  BookingSummary,
  Campground,
  CheckoutStepChooser,
  FrequentlyAskedQuestions,
  NotFound,
  NotBookable,
  StripePayment,
} from '@/views'

Vue.use(Router)

/**
 * This is a helper function the get the first unfulfilled step.
 * @returns {number}
 */
const firstUnfulfilled = () => {
  const steps = store.getters['application/steps']
  let last = Object.keys(steps).length
  Object.keys(steps).forEach(step => {
    const s = steps[step]
    /* istanbul ignore else */
    if (!s.fulfilled && s.id < last) last = s.id
  })
  return last
}

/**
 * This function checks is allowed to access the step by checking
 * the fulfilled property in the store. If the user is not allowed
 * he gets redirected to the last unfulfilled step.
 * @param to
 * @param from
 * @param next
 */
const validateRouteStep = (to, from, next) => {
  const firstUnfulfilledStep = firstUnfulfilled()
  const paramStep = parseInt(to.params.step)

  // query parameter handling
  const dateFrom = to.query.date_from
  const dateUntil = to.query.date_until
  let adults = to.query.adults
  let children = to.query.children
  let acco = to.query.acco

  if (adults !== null && adults !== undefined && adults.match(/^\d+$/)) {
    adults = parseInt(adults, 10)
  } else {
    adults = 0
  }
  if (children !== null && children !== undefined && children.match(/^\d+$/)) {
    children = parseInt(children, 10)
  } else {
    children = 0
  }
  if (acco !== null && acco !== undefined && acco.match(/^\d+$/)) {
    acco = parseInt(acco, 10)
  } else {
    acco = null
  }

  const now = new Date()
  now.setHours(0, 0, 0, 0)

  /* istanbul ignore else */
  if (dateFrom || dateUntil) {
    let normalizedFrom = null
    let normalizedUntil = null

    if (dateFrom) {
      const df = DateTime.fromISO(dateFrom).toJSDate()
      // if date is in the past, take today
      normalizedFrom = df >= now ? df : new Date()
    }

    if (dateUntil) {
      const du = DateTime.fromISO(dateUntil).toJSDate()
      normalizedUntil = du >= now ? du : null
    }

    store.dispatch('booking/setDates', [normalizedFrom, normalizedUntil])

    if (dateFrom && dateUntil) {
      store.dispatch(
        'application/setStepStatus',
        // eslint-disable-next-line
        {number: 1, key: 'displaySelection', value: 'persons'});
    }
  }

  if (adults || children || acco) {
    store.dispatch('booking/setRequestedTravelData', { acco, adults, children })
  }

  if (paramStep === firstUnfulfilledStep || paramStep < firstUnfulfilledStep) {
    if (paramStep === 2) {
      store.dispatch('campground/getAvailabilities').then(result => {
        if (result) {
          next()
        }
      })
    } else if (paramStep === 3) {
      store.dispatch('campground/getExtraPrices').then(result => {
        /* istanbul ignore else */
        if (result) {
          next()
        }
      })
    } else {
      next()
    }
  } else {
    // redirect to last unfulfilled and keep params
    next({
      name: 'Step',
      params: { ...to.params, step: firstUnfulfilledStep.toString() },
      query: { ...to.query },
    })
  }
}

export const beforeEach = (to, from, next) => {
  // ----- Global language prefix handling (paths without locale or with unsupported locale) -----
  const SUPPORTED_LANGS = Object.keys(messages)
  const DEFAULT_LANG = 'en'
  const path = to.path || ''
  const firstSegMatch = path.match(/^\/([^/]+)(?:\/|$)/)
  const firstSeg = firstSegMatch ? firstSegMatch[1] : ''
  const hasSupportedLangPrefix = SUPPORTED_LANGS.includes(firstSeg)

  if (!hasSupportedLangPrefix) {
    // Root path → /en
      if (!firstSeg) {
        return next({ path: `/${DEFAULT_LANG}`, replace: true })
      }

    // If the first segment looks like a language code but is unsupported → replace it with DEFAULT_LANG
      const looksLikeLang = /^[A-Za-z]{2,5}(?:-[A-Za-z0-9]{2,8})?$/.test(firstSeg)
      if (looksLikeLang) {
        const newPath = path.replace(/^\/[^/]+/, `/${DEFAULT_LANG}`)
        return next({ path: newPath, query: to.query, hash: to.hash, replace: true })
      }

    // Otherwise, there is no language prefix at all → prefix DEFAULT_LANG
      return next({ path: `/${DEFAULT_LANG}${path}`, query: to.query, hash: to.hash, replace: true })
    }

  // Validate language param and fallback to 'en' if missing/unsupported
  const lang = to.params && to.params.language
  // Only enforce when the target route expects a :language param
  const expectsLanguageParam = to.matched && to.matched.some(r => r.path && r.path.includes('/:language'))

  if (expectsLanguageParam && (!lang || !SUPPORTED_LANGS.includes(lang))) {
    // Try to keep the same named route (preserves children) by swapping the param
    if (to.name) {
      return next({
        name: to.name,
        params: { ...to.params, language: DEFAULT_LANG },
        query: to.query,
        hash: to.hash,
        replace: true,
      })
    }
    // Fallback: rewrite path by replacing the first segment with /en
    const newPath = `/${DEFAULT_LANG}${to.path.replace(/^\/[^/]+/, '')}`
    return next({ path: newPath, query: to.query, hash: to.hash, replace: true })
  }

  let campground = store.getters['campground/campground']

  // Ensure campground data is available when entering StripePayment directly
  if (to.name === 'StripePayment') {
    const toCampgroundSlug = to.params.campground
    const toLang = to.params.language
    const { code } = store.getters['application/language']

    // set aid
    /* istanbul ignore else */
    if (to.query.aid && to.query.aid !== store.state.router.query.aid) {
      const queryAid = to.query.aid
      if (uuidValidate(queryAid)) {
        store.dispatch('booking/setAid', queryAid)
      } else {
        let referer = null
        try {
          referer = document.referrer
        } catch (e) {
          console.log('error:', e)
        }

        logWarning(`Not a valid UUID: ${queryAid}`, {
          referer: referer,
        })
      }
    }

    if (to.query.gid) {
      store.dispatch('booking/setGid', to.query.gid)
    }

    // persist draftbooking params early so API calls can use them
    if (to.query.draftbooking) {
      store.dispatch('booking/setDraftbookingToken', to.query.draftbooking)
    }
    if (to.query.draft_booking_id) {
      store.dispatch('booking/setDraftBookingId', to.query.draft_booking_id)
    }

    // set token
    /* istanbul ignore else */
    if (to.query.token) {
      store.dispatch('booking/setToken', to.query.token)
    }

    // if no campground data are in the store or slug/lang mismatches or draftbooking token is present
    if (campground === null || campground.slug !== toCampgroundSlug || toLang !== code || to.query.draftbooking) {
      // if campground has changed, reset all data
      if (campground !== null && toCampgroundSlug !== campground.slug) {
        store.dispatch('application/resetState')
        store.dispatch('booking/resetState')
        store.dispatch('campground/resetState')
      }

      // if language has changed
      if (toLang !== code) {
        store.dispatch('application/setLanguageSlug', toLang)
      }

      // get new campground data
      return store
        .dispatch('campground/getCampgroundData', toLang)
        .then(result => {
          if (result) {
            store.dispatch('application/setTestmode', !!result.testmode)

            if (!result.available) {
              if (result.redirect) {
                /* if not available, redirect to given language url */
                next({
                  name: to.name,
                  params: { ...to.params, language: result.redirect },
                  query: to.query,
                })
                store.dispatch('application/setLanguageSlug', result.redirect)
              } else {
                next({
                  name: 'CampNotBookable',
                  params: {
                    ...to.params,
                    noSeasons: result.no_seasons === true,
                  },
                })
              }
              return
            }

            // only fetch attributes if data are available
            if (result.campground && result.campground.slug) {
              store.dispatch('campground/getAttributes', result.campground.slug)
            }

            campground = store.getters['campground/campground']

            // If we just loaded a draft booking, redirect to step 2 and clean up URL
            const draft = result.draft_booking_data || result.draft_booking
            if (draft && to.query.draftbooking) {
              const newQuery = { ...to.query }
              delete newQuery.draftbooking
              return next({
                name: 'Step',
                params: { ...to.params, step: '2' },
                query: newQuery,
                replace: true,
              })
            }

            // Proceed to StripePayment now that base data are ensured
            next()
          } else {
            store.dispatch('application/setTestmode', false)
            next({ name: 'CampNotFound', params: to.params })
          }
        })
        .catch(() => {
          store.dispatch('application/setTestmode', false)
          next({ name: 'CampNotFound', params: to.params })
        })
    } else {
      // Data already present
      next()
    }
    return
  }

  if (to.name === 'Step' || to.name === 'Campground') {
    const toCampgroundSlug = to.params.campground
    const toLang = to.params.language
    const { code } = store.getters['application/language']

    // set aid
    /* istanbul ignore else */
    if (to.query.aid && to.query.aid !== store.state.router.query.aid) {
      const queryAid = to.query.aid
      if (uuidValidate(queryAid)) {
        store.dispatch('booking/setAid', queryAid)
      } else {
        let referer = null
        try {
          referer = document.referrer
        } catch (e) {
          console.log('error:', e)
        }

        logWarning(`Not a valid UUID: ${queryAid}`, {
          referer: referer,
        })
      }
    }

    if (to.query.gid) {
      store.dispatch('booking/setGid', to.query.gid)
    }

    // persist draftbooking params early so API calls can use them
    if (to.query.draftbooking) {
      store.dispatch('booking/setDraftbookingToken', to.query.draftbooking)
    }
    if (to.query.draft_booking_id) {
      store.dispatch('booking/setDraftBookingId', to.query.draft_booking_id)
    }

    // set token
    /* istanbul ignore else */
    if (to.query.token) {
      store.dispatch('booking/setToken', to.query.token)
    }

    // if no campground data are in the store or slug/lang mismatches or draftbooking token is present
    if (campground === null || campground.slug !== toCampgroundSlug || toLang !== code || to.query.draftbooking) {
      // if campground has changed, reset all data
      if (campground !== null && toCampgroundSlug !== campground.slug) {
        store.dispatch('application/resetState')
        store.dispatch('booking/resetState')
        store.dispatch('campground/resetState')
      }

      // if language has changed
      if (toLang !== code) {
        store.dispatch('application/setLanguageSlug', toLang)
      }

      // get new campground data
      return store
        .dispatch('campground/getCampgroundData', toLang)
        .then(result => {
          if (result) {
            store.dispatch('application/setTestmode', !!result.testmode)
            if (!result.available) {
              if (result.redirect) {
                /* if not available, redirect to given language url */
                next({
                  name: to.name,
                  params: { ...to.params, language: result.redirect },
                  query: to.query,
                })
                store.dispatch('application/setLanguageSlug', result.redirect)
              } else {
                next({
                  name: 'CampNotBookable',
                  params: {
                    ...to.params,
                    noSeasons: result.no_seasons === true,
                  },
                })
              }
              return
            }

            // only now fetch attributes when campground data exist and are available
            if (result.campground && result.campground.slug) {
              store.dispatch('campground/getAttributes', result.campground.slug)
            }

            campground = store.getters['campground/campground']

            // If we just loaded a draft booking, redirect to step 2 and clean up URL
            const draft = result.draft_booking_data || result.draft_booking
            if (draft && to.query.draftbooking) {
              const newQuery = { ...to.query }
              delete newQuery.draftbooking
              return next({
                name: 'Step',
                params: { ...to.params, step: '2' },
                query: newQuery,
                replace: true,
              })
            }

            validateRouteStep(to, from, next)
          } else {
            store.dispatch('application/setTestmode', false)
            next({ name: 'CampNotFound', params: to.params })
          }
        })
        .catch(() => {
          store.dispatch('application/setTestmode', false)
          next({ name: 'CampNotFound', params: to.params })
        })
    } else {
      validateRouteStep(to, from, next)
    }
  } else if (to.name === 'Confirmation') {
    /* basic campground data */
    store.dispatch('application/setLoader', true)

    return store.dispatch('campground/getCampgroundData', to.params.language).then(result => {
      /* istanbul ignore else */
      campground = store.getters['campground/campground']
      if (result) {
        if (!result.available) {
          /* if not available, redirect to given language url */
          /* reset booking store */
          store.dispatch('application/resetStepStatus')
          store.dispatch('booking/resetState')
          next({ name: to.name, params: { ...to.params, language: result.redirect } })
          store.dispatch('application/setLanguageSlug', result.redirect)
        } else {
          return store
            .dispatch('booking/getBookingDetails', to.params.bookingId)
            .then(bookingData => {
              if (bookingData.booking_status === 'internally_canceled') {
                /* reset booking store */
                store.dispatch('application/resetStepStatus')
                store.dispatch('booking/resetState')
                store.dispatch('booking/setBookingDetails', bookingData)
                next({ name: 'ConfirmationTimeout', params: { ...to.params } })
              } else if (bookingData.booking_status === 'pending' && !bookingData.payment.success) {
                // console.log("Resetting to step 5", bookingData.payment.success, bookingData)
                next({
                  name: 'Step',
                  params: { ...to.params, step: 5 },
                  query: { ...to.query },
                })
              } else if (
                bookingData.booking_status !== 'confirmed' &&
                !bookingData.payment.success
              ) {
                /* reset booking store */
                store.dispatch('application/resetStepStatus')
                store.dispatch('booking/resetState')
                store.dispatch('booking/setBookingDetails', bookingData)
                next({ name: 'ConfirmationGenericError', params: { ...to.params } })
              } else {
                /* reset booking store */
                store.dispatch('application/resetStepStatus')
                store.dispatch('booking/resetState')
                store.dispatch('booking/setBookingDetails', bookingData)

                try {
                  const confirmation = store.getters['booking/confirmation']
                  // Find out campsite type from pricelist
                  let selectedCampsiteType = 'tent'
                  if (confirmation.pricelist && confirmation.pricelist.components) {
                    Object.keys(confirmation.pricelist.components).forEach(idx => {
                      const component = confirmation.pricelist.components[idx]
                      if (component.object_type === 'campsite') {
                        selectedCampsiteType = component.object_subtype
                      }
                    })
                  }
                  GTM.track({
                    event: 'BookingConfirmation',
                    transactionId: to.params.bookingId,
                    transactionAffiliation: 'Max Camping',
                    transactionTotal: confirmation.pricelist.total,
                    currency: confirmation.pricelist.currency,
                    transactionTax: 0,
                    transactionProducts: [
                      {
                        sku: campground.slug,
                        name: campground.name,
                        category: selectedCampsiteType,
                        price: confirmation.pricelist.total,
                        quantity: 1,
                      },
                    ],
                  })
                } catch (e) {
                  logException(e)
                }

                next()
              }
            })
            .catch(() => {
              /* reset booking store */
              store.dispatch('application/resetStepStatus')
              store.dispatch('booking/resetState')
              next({ name: 'ConfirmationNotFound', params: { ...to.params } })
            })
        }
      } else {
        store.dispatch('application/resetStepStatus')
        store.dispatch('booking/resetState')
        next({ name: 'CampNotFound', params: to.params })
      }
    })
  } else {
    // set page title
    /* istanbul ignore else */
    if (to.meta.title) {
      document.title = router.app.$t(to.meta.title)
    }
    next()
  }
}

export const afterEach = to => {
  /* istanbul ignore else */
  if (to.name === 'Step' || to.name === 'Campground') {
    store.dispatch('application/setStep', parseInt(to.params.step))
  }

  store.dispatch('application/setLoader', false)
  store.dispatch('application/setTitle')
  // TODO: HotJar trigger (ink. split für step-1 und step-4)
}

// export router used in main.js
export const router = new Router({
  mode: 'history',
  linkActiveClass: 'is-active',
  routes: [
    {
      path: '*',
      name: 'Error',
      components: {
        aside: false,
        main: NotFound,
      },
    },
    {
      path: '/:language/frequently-asked-questions',
      name: 'FAQ',
      components: {
        aside: false,
        main: FrequentlyAskedQuestions,
      },
      meta: {
        title: 'pages.faq.pageTitle',
      },
    },
    {
      path: '/:language/:campground',
      name: 'Campground',
      components: {
        aside: BookingSummary,
        main: Campground,
      },
      children: [
        {
          path: ':step',
          name: 'Step',
          component: CheckoutStepChooser,
          props: route => ({ step: route.params.step }),
        },
        {
          path: 'stripe-payment/:bookingId',
          name: 'StripePayment',
          component: StripePayment,
        },
      ],
    },
    {
      path: '/:language/:campground',
      name: 'CampNotFound',
      components: {
        aside: false,
        main: NotFound,
      },
    },
    {
      path: '/:language/:campground',
      name: 'CampNotBookable',
      components: {
        aside: false,
        main: NotBookable,
      },
      props: { aside: false, main: true },
    },
    {
      path: '/:language/:campground/confirmation/:bookingId',
      name: 'Confirmation',
      components: {
        aside: false,
        main: BookingConfirmation,
      },
    },
    {
      path: '/:language/:campground/confirmation/:bookingId',
      name: 'ConfirmationNotFound',
      components: {
        aside: false,
        main: NotFound,
      },
    },
    {
      path: '/:language/:campground/confirmation/:bookingId',
      name: 'ConfirmationTimeout',
      components: {
        aside: false,
        main: BookingConfirmationTimeout,
      },
    },
    {
      path: '/:language/:campground/confirmation/:bookingId',
      name: 'ConfirmationGenericError',
      components: {
        aside: false,
        main: BookingConfirmationGenericError,
      },
    },
    {
      path: '/:language/:campground/partial-confirmation/:bookingId',
      redirect: to => ({
        name: 'Confirmation',
        params: {
          language: to.params.language,
          campground: to.params.campground,
          bookingId: to.params.bookingId,
        },
        query: to.query,
      }),
    },
  ],
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
})

// add router beforeEach and afterEach hooks
router.beforeEach((to, from, next) => beforeEach(to, from, next))
router.afterEach(to => afterEach(to))
